// Single certification checkbox used throughout
// the Underwriting tab. Wraps a checkbox + sign-confirmation modal +
// the cert post; consumers configure tab/section/code/label.
//
// Once a certification row exists in UnderwritingCertifications for the
// given (tab, section, certificationCode) the checkbox is "signed" and
// the cert is read-only (no un-sign).

import { useState } from "react";
import { Check, Lock } from "lucide-react";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { CertConfirmationModal } from "./CertConfirmationModal";
import {
  usePostUnderwritingCertification,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import type { DealFlowTab } from "@/api/dealFlow/types";
import { useInternalUserLabels, fallbackUserLabel } from "@/api/internalUsers";
import { useDateFormatter } from "@/utils/datetime";

interface UnderwritingCertificationCheckboxProps {
  dealId: string;
  tab: DealFlowTab;
  section: number;
  certificationCode: string;
  label: string;
  /** Modal header. Defaults to `Sign certification: ${certificationCode}` if omitted. */
  title?: string;
  /** Attestation language shown in the confirmation modal. Defaults to the
   *  generic "I attest…" wording if omitted. */
  attestationText?: string;
  /** When true (e.g. waiting on a backend gate to clear) the checkbox is
   *  disabled even if the cert is otherwise eligible to sign. */
  disabled?: boolean;
  /** Optional reason shown as a tooltip when disabled — e.g. "Deal Outcome
   *  must be finalized first" for the Section 8 certs. */
  disabledReason?: string;
  /** Optional callback once a signature succeeds. */
  onSigned?: () => void;
}

export function UnderwritingCertificationCheckbox({
  dealId,
  tab,
  section,
  certificationCode,
  label,
  title,
  attestationText,
  disabled = false,
  disabledReason,
  onSigned,
}: UnderwritingCertificationCheckboxProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const certs = useUnderwritingCertifications(dealId, tab, section);
  const signed = (certs.data ?? []).find(
    (c) => c.certificationCode === certificationCode,
  );

  const post = usePostUnderwritingCertification(dealId);
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  // Resolve the signer id to a display name — never render a raw GUID.
  const signerLabels = useInternalUserLabels(
    signed?.signedByUserId ? [signed.signedByUserId] : [],
  );
  const signerName = signed?.signedByUserId
    ? (signerLabels.lookup(signed.signedByUserId)?.displayName ??
      fallbackUserLabel(signed.signedByUserId))
    : "—";

  const onConfirm = async () => {
    try {
      await post.mutateAsync({
        tab,
        section,
        certificationCode,
        label,
        justification: null,
      });
      toast.success("Certification signed.");
      setModalOpen(false);
      onSigned?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not sign certification.",
      );
    }
  };

  const isSigned = !!signed;
  const baseClass =
    "flex items-start gap-space-2 text-small w-full text-left p-space-2 rounded-md transition-colors";
  const interactiveClass = isSigned
    ? "bg-status-success/10"
    : disabled
      ? "opacity-50 cursor-not-allowed"
      : "hover:bg-white-warm cursor-pointer";

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && !isSigned && setModalOpen(true)}
        disabled={disabled || isSigned}
        title={
          isSigned
            ? `Signed by ${signerName} on ${signed.signedAt ? dateTime(signed.signedAt) : "—"}`
            : disabled
              ? disabledReason
              : undefined
        }
        className={`${baseClass} ${interactiveClass}`}
      >
        <span
          className={`mt-[2px] inline-flex shrink-0 items-center justify-center h-5 w-5 rounded-sm border ${
            isSigned
              ? "bg-status-success border-status-success text-white-pure"
              : "bg-white-pure border-border-light"
          }`}
          aria-hidden
        >
          {isSigned ? (
            <Check size={14} />
          ) : disabled ? (
            <Lock size={12} className="text-gray-mid" />
          ) : null}
        </span>
        <span className="flex-1">
          <span className="block text-onyx">{label}</span>
          {isSigned && (
            <span className="block text-caption text-gray-mid mt-[2px]">
              Signed{" "}
              {signed.signedAt ? dateTime(signed.signedAt) : "—"}
            </span>
          )}
          {!isSigned && disabled && disabledReason && (
            <span className="block text-caption text-gray-mid mt-[2px]">
              {disabledReason}
            </span>
          )}
        </span>
      </button>
      <CertConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={title ?? `Sign certification: ${certificationCode}`}
        attestationText={
          attestationText ??
          `You attest that the following condition is satisfied:\n\n${label}\n\nThis ceremony is permanent and recorded in the audit log.`
        }
        onConfirm={onConfirm}
        pending={post.isPending}
      />
    </>
  );
}
